import { useState } from 'react';
import { OctagonAlert } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/api/client';
import { useScope } from '@/context/ScopeContext';
import TESTIDS from '@/constants/testIds';
import { toast } from 'sonner';

export function EmergencyStopButton() {
    const { globalEmergencyStop, setGlobalEmergencyStop } = useScope();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const toggle = async () => {
        setBusy(true);
        try {
            const next = !globalEmergencyStop;
            await apiClient.post('/automation/emergency_stop', {
                scope: 'global',
                enabled: next,
            });
            setGlobalEmergencyStop(next);
            toast.success(
                next
                    ? 'Global emergency stop enabled — publishing & scheduling halted.'
                    : 'Global emergency stop disabled — operations resumed.',
            );
            setOpen(false);
        } catch (e) {
            toast.error('Failed to toggle emergency stop');
        } finally {
            setBusy(false);
        }
    };

    const isOn = globalEmergencyStop;

    return (
        <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogTrigger asChild>
                <Button
                    variant={isOn ? 'destructive' : 'outline'}
                    size="sm"
                    data-testid={TESTIDS.emergencyStopButton}
                    className={
                        isOn
                            ? ''
                            : 'border-[hsl(var(--danger)/0.4)] text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger)/0.08)] hover:text-[hsl(var(--danger))]'
                    }
                >
                    <OctagonAlert className="mr-1.5 h-3.5 w-3.5" />
                    {isOn ? 'Stop active' : 'Emergency stop'}
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {isOn ? 'Lift global emergency stop?' : 'Engage global emergency stop?'}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {isOn
                            ? 'Re-enables scheduling and publishing across all accounts. Make sure the situation that caused the stop is resolved.'
                            : 'Halts all publishing, scheduling, and outbound reply actions across every connected account immediately.'}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid={TESTIDS.emergencyStopCancel}>
                        Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                        disabled={busy}
                        onClick={(e) => {
                            e.preventDefault();
                            toggle();
                        }}
                        data-testid={TESTIDS.emergencyStopConfirm}
                    >
                        {isOn ? 'Lift stop' : 'Engage stop'}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
